<template>
  <Teleport to="body">
    <transition name="guestinvite">
      <div
        v-if="dialog"
        class="guestinvite"
        role="dialog"
        aria-modal="true"
        @mousedown.self="close"
      >
        <div class="guestinvite__panel">
          <header class="guestinvite__head">
            <h3 class="guestinvite__title">Invite guest</h3>
            <button
              type="button"
              class="guestinvite__close"
              title="Sluiten"
              @click="close"
            >
              ×
            </button>
          </header>

          <div v-if="created" class="guestinvite__body">
            <p class="guestinvite__intro">
              <strong>{{ createdEmail }}</strong> now has guest access to this
              project until <strong>{{ formatDate(expiresAtMs) }}</strong
              >. Alice doesn't email invites — send them the text below
              yourself.
            </p>
            <label class="guestinvite__field">
              <span class="guestinvite__label">Invite message</span>
              <textarea
                class="guestinvite__textarea"
                rows="6"
                readonly
                :value="inviteText"
                @focus="$event.target.select()"
              ></textarea>
            </label>
            <button
              type="button"
              class="guestinvite__save"
              @click="copyInviteText"
            >
              {{ copied ? "Copied!" : "Copy invite message" }}
            </button>
          </div>

          <div v-else class="guestinvite__body">
            <p class="guestinvite__intro">
              Give an external researcher (e.g. at the client) access to
              <strong>only this project</strong>. They sign in with an email
              link — no Google account needed. Access ends automatically.
            </p>

            <label class="guestinvite__field">
              <span class="guestinvite__label">Email address</span>
              <input
                v-model="email"
                class="guestinvite__input"
                type="email"
                placeholder="name@client.com"
                :disabled="loading"
              />
            </label>

            <div class="guestinvite__field">
              <span class="guestinvite__label">Access duration</span>
              <div class="guestinvite__chips">
                <button
                  v-for="d in [7, 14, 30, 60]"
                  :key="d"
                  type="button"
                  class="guestinvite__chip"
                  :class="{ 'guestinvite__chip--on': days === d }"
                  @click="setDays(d)"
                >
                  {{ d }} days
                </button>
              </div>
              <input
                v-model="endDate"
                class="guestinvite__input"
                type="date"
                :min="minDate"
                :max="maxDate"
                :disabled="loading"
              />
              <span class="guestinvite__hint"
                >Ends at the end of that day — at most 60 days from now.</span
              >
            </div>

            <label class="guestinvite__field">
              <span class="guestinvite__label">AI token budget</span>
              <input
                v-model.number="tokenBudget"
                class="guestinvite__input"
                type="number"
                min="1"
                step="100000"
                :disabled="loading"
              />
              <span class="guestinvite__hint"
                >Total for the whole access period. 2,000,000 ≈ a few weeks of
                regular chatting; an admin can raise it later.</span
              >
            </label>

            <p v-if="error" class="guestinvite__error">{{ error }}</p>
          </div>

          <footer class="guestinvite__foot">
            <button type="button" class="guestinvite__cancel" @click="close">
              {{ created ? "Close" : "Annuleren" }}
            </button>
            <button
              v-if="!created"
              type="button"
              class="guestinvite__save"
              :disabled="!canSubmit"
              @click="submit"
            >
              {{ loading ? "Inviting…" : "Invite" }}
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_DAYS = 60;

export default {
  name: "GuestInviteDialog",
  props: {
    dialog: { type: Boolean, default: false },
    projectId: { type: String, required: true },
    projectTitle: { type: String, default: "" },
  },
  emits: ["close", "created"],
  data() {
    return {
      email: "",
      days: 30,
      endDate: this.dateStr(Date.now() + 30 * DAY_MS),
      tokenBudget: 2000000,
      loading: false,
      error: null,
      created: false,
      createdEmail: "",
      expiresAtMs: null,
      copied: false,
    };
  },
  computed: {
    minDate() {
      return this.dateStr(Date.now() + DAY_MS);
    },
    maxDate() {
      return this.dateStr(Date.now() + MAX_DAYS * DAY_MS);
    },
    canSubmit() {
      return (
        !this.loading &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim()) &&
        !!this.endDate
      );
    },
    inviteText() {
      const title = this.projectTitle || "our project";
      return (
        `You've been invited to collaborate on "${title}" in Alice, ` +
        `Valsplat's research workspace.\n\n` +
        `1. Go to ${window.location.origin}/guest\n` +
        `2. Enter this email address (${this.createdEmail})\n` +
        `3. Open the sign-in link you receive — you're in.\n\n` +
        `Your access runs until ${this.formatDate(this.expiresAtMs)}.`
      );
    },
  },
  watch: {
    dialog(open) {
      if (open) {
        this.reset();
        document.addEventListener("keydown", this.onKeydown);
      } else {
        document.removeEventListener("keydown", this.onKeydown);
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e) {
      if (e.key === "Escape") this.close();
    },
    close() {
      this.$emit("close");
    },
    reset() {
      this.email = "";
      this.days = 30;
      this.endDate = this.dateStr(Date.now() + 30 * DAY_MS);
      this.tokenBudget = 2000000;
      this.loading = false;
      this.error = null;
      this.created = false;
      this.createdEmail = "";
      this.expiresAtMs = null;
      this.copied = false;
    },
    dateStr(ms) {
      return new Date(ms).toISOString().slice(0, 10);
    },
    setDays(d) {
      this.days = d;
      this.endDate = this.dateStr(Date.now() + d * DAY_MS);
    },
    formatDate(ms) {
      if (!ms) return "";
      return new Date(ms).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },
    // End of the chosen day, local time, capped at now + 60 days.
    expiresAtFromEndDate() {
      const [y, m, d] = this.endDate.split("-").map(Number);
      const endOfDay = new Date(y, m - 1, d, 23, 59, 59).getTime();
      return Math.min(endOfDay, Date.now() + MAX_DAYS * DAY_MS);
    },
    async submit() {
      if (!this.canSubmit) return;
      this.loading = true;
      this.error = null;
      const expiresAt = this.expiresAtFromEndDate();
      try {
        const createGuestInvite = httpsCallable(functions, "createGuestInvite");
        await createGuestInvite({
          projectId: this.projectId,
          email: this.email.trim().toLowerCase(),
          expiresAt,
          tokenBudget: this.tokenBudget,
        });
        this.createdEmail = this.email.trim().toLowerCase();
        this.expiresAtMs = expiresAt;
        this.created = true;
        this.$emit("created");
      } catch (err) {
        console.error("createGuestInvite failed:", err);
        this.error =
          err?.message?.replace(/^Firebase: /, "") ||
          "Couldn't create the invite. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async copyInviteText() {
      try {
        await navigator.clipboard.writeText(this.inviteText);
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
      } catch (err) {
        console.error("clipboard write failed:", err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.guestinvite {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(20, 25, 25, 0.55);

  &__panel {
    width: 100%;
    max-width: 560px;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    background: var(--vp-white);
    border: 1px solid var(--vp-black);
    border-radius: 0;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
    background: var(--vp-black);
    color: var(--vp-white);
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1;
  }

  &__close {
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--font-display);
    font-size: 26px;
    line-height: 1;
    color: var(--vp-white);
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }

  &__body {
    padding: 24px 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__intro {
    margin: 0;
    font-family: var(--font-body);
    font-size: 14px;
    line-height: 1.6;
    color: var(--fg-2);
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-family: var(--font-ui);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fg-3);
  }

  &__input,
  &__textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border-hairline);
    border-radius: 0;
    outline: none;
    background: var(--vp-white);
    font-family: var(--font-body);
    font-size: 15px;
    line-height: 1.5;
    color: var(--vp-black);

    &:focus {
      border-color: var(--vp-black);
    }
  }

  &__textarea {
    resize: vertical;
  }

  &__hint {
    font-family: var(--font-ui);
    font-size: 12px;
    color: var(--fg-3);
  }

  &__chips {
    display: flex;
    gap: 8px;
    margin-bottom: 6px;
  }

  &__chip {
    padding: 6px 12px;
    background: var(--vp-white);
    border: 1px solid var(--border-hairline);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 13px;
    color: var(--vp-black);

    &:hover {
      border-color: var(--vp-black);
    }

    &--on {
      background: var(--vp-black);
      color: var(--vp-white);
      border-color: var(--vp-black);
    }
  }

  &__error {
    margin: 0;
    font-family: var(--font-body);
    font-size: 14px;
    color: var(--vp-rood-100, #d43c3c);
  }

  &__foot {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 20px;
    border-top: 1px solid var(--border-hairline);
  }

  &__cancel {
    padding: 8px 16px;
    background: var(--vp-white);
    border: 1px solid var(--vp-black);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--vp-black);

    &:hover {
      background: var(--vp-black);
      color: var(--vp-white);
    }
  }

  &__save {
    padding: 8px 18px;
    background: var(--vp-black);
    color: var(--vp-white);
    border: 1px solid var(--vp-black);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;

    &:hover:not(:disabled) {
      background: var(--accent-100);
      color: var(--vp-black);
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}

.guestinvite-enter-active {
  transition: opacity var(--dur-moderate) var(--ease-out);

  .guestinvite__panel {
    transition: transform var(--dur-moderate) var(--ease-out),
      opacity var(--dur-moderate) var(--ease-out);
  }
}
.guestinvite-leave-active {
  transition: opacity var(--dur-exit) var(--ease-out);

  .guestinvite__panel {
    transition: transform var(--dur-exit) var(--ease-out),
      opacity var(--dur-exit) var(--ease-out);
  }
}
.guestinvite-enter-from,
.guestinvite-leave-to {
  opacity: 0;

  .guestinvite__panel {
    opacity: 0;
    transform: scale(0.96);
  }
}
</style>
